import { StringEnum as TypeboxStringEnum } from '@feathersjs/typebox'

// Single source of truth for staff (employee) roles.
// Add a new role here + its labels below and the user/order schemas,
// role pickers, payroll groupings, sort order and the (type-checked)
// colour maps all pick it up.
//
// Ordered the way roles should be sorted/grouped in pickers and the
// shifts axis: management first, then kitchen, hall, logistics, rigs.
// `customer` is intentionally NOT here — it is a separate profile type,
// not a staff role.
export const STAFF_ROLE_VALUES = [
  'manager',
  'cook',
  'waiter',
  'barmen',
  'dishwasher',
  'deliveryman',
  'workstation',
] as const

export type StaffRole = (typeof STAFF_ROLE_VALUES)[number]

// Roles excluding the non-human `workstation` rig — used by staff lists,
// shift staff browsers and role pickers that only deal with people.
export const STAFF_ROLE_VALUES_HUMAN = STAFF_ROLE_VALUES.filter(
  (role) => role !== 'workstation',
)

export const StaffRoleEnum = TypeboxStringEnum([...STAFF_ROLE_VALUES])

// Singular labels for role pickers.
export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  manager: 'Manager',
  cook: 'Cook',
  waiter: 'Waiter',
  barmen: 'Barmen',
  dishwasher: 'Dishwasher',
  deliveryman: 'Delivery man',
  workstation: 'Workstation',
}

// Plural labels for payroll / dashboard groupings ("Bartenders", not "Barmens").
export const STAFF_ROLE_LABELS_PLURAL: Record<StaffRole, string> = {
  manager: 'Managers',
  cook: 'Cooks',
  waiter: 'Waiters',
  barmen: 'Bartenders',
  dishwasher: 'Dishwashers',
  deliveryman: 'Deliverymen',
  workstation: 'Workstations',
}

// Sort weight by role — index in the canonical order. Unknown roles sort last.
export const getStaffRoleOrder = (role: string | undefined): number => {
  const index = STAFF_ROLE_VALUES.indexOf(role as StaffRole)

  return index === -1 ? STAFF_ROLE_VALUES.length : index
}

// Build `{ value, label }` option lists for pickers. Pass a subset to
// restrict/reorder; defaults to every staff role in canonical order.
export const getStaffRoleOptions = (
  roles: readonly StaffRole[] = STAFF_ROLE_VALUES,
) => roles.map((value) => ({ value, label: STAFF_ROLE_LABELS[value] }))

export const STAFF_ROLE_OPTIONS = getStaffRoleOptions()
